import React, { useMemo, useRef, useState, useEffect } from "react"
import WizImg from "../../../../../assets/avia/sidebar/wizair.png"
import PlaneImg from "../../../../../assets/avia/sidebar/air.png"
import SearchInput from "./SearchInput"
import { NUMBER_OF_VIEWS } from "../../utils/variables/sidebar"
import ShowMore from "./ShowMore"
import ItemsFlexWrapper from "./ItemsFlexWrapper"
import CircularCheckbox from "../ui/CircularCheckbox"
import { useTranslation } from "react-i18next"
import { toggleId } from "../../utils/filters/common"
import { motion } from "framer-motion"
import AnimatedList from "./AnimatedList"
import { useClickOutside } from "../../hooks"

const ITEMS_PER_LOAD = 5
const MAX_HEIGHT = 400
const SEARCH_DEBOUNCE = 200

const Airlines = ({ airlines, onChange, selectedCodes }) => {
  const { t } = useTranslation()

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [visibleCount, setVisibleCount] = useState(NUMBER_OF_VIEWS)
  const [shouldScroll, setShouldScroll] = useState(false)

  const sectionRef = useRef(null)
  const listContainerRef = useRef(null)

  useClickOutside(sectionRef, () => setVisibleCount(NUMBER_OF_VIEWS))

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search)
    }, SEARCH_DEBOUNCE)

    return () => clearTimeout(id)
  }, [search])

  const extendedAirlines = useMemo(() => {
    return [
      ...airlines,
      {
        name: t("admin.anonymous_airline"),
        iata: "UNKNOWN",
      },
    ]
  }, [airlines, t])

  // сортируем один раз
  const sortedAirlines = useMemo(() => {
    return extendedAirlines
      .filter((a) => a.iata !== "UNKNOWN")
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [extendedAirlines])

  // const filteredAirlines = useMemo(() => {
  //   const query = debouncedSearch.toLowerCase()

  //   if (!query) {
  //     return [...sortedAirlines, extendedAirlines.find((a) => a.iata === "UNKNOWN")]
  //   }

  //   const filtered = sortedAirlines.filter((a) => a.name.toLowerCase().includes(query))

  //   const unknown = extendedAirlines.find((a) => a.iata === "UNKNOWN")

  //   return [...filtered, unknown]
  // }, [sortedAirlines, extendedAirlines, debouncedSearch])

  const filteredAirlines = useMemo(() => {
    const query = debouncedSearch.toLowerCase()
    if (!query) return sortedAirlines

    return sortedAirlines.filter((a) => a.name.toLowerCase().includes(query))
  }, [sortedAirlines, debouncedSearch])

  const isAllSelected = useMemo(() => {
    return selectedCodes.length === extendedAirlines.length
  }, [selectedCodes, extendedAirlines])

  const handleAirlineChange = (code) => {
    if (code === "all") {
      const allIds = isAllSelected ? [] : extendedAirlines.map((item) => item.iata)

      onChange({ key: "airlinesCodes", value: allIds })
      return
    }

    const updated = selectedCodes.includes(code)
      ? selectedCodes.filter((id) => id !== code)
      : [...selectedCodes, code]

    onChange({
      key: "airlinesCodes",
      value: updated,
    })
  }

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + ITEMS_PER_LOAD, filteredAirlines.length))
    setShouldScroll(true)
  }

  useEffect(() => {
    if (shouldScroll && listContainerRef.current) {
      setTimeout(() => {
        listContainerRef.current?.scrollTo({
          top: listContainerRef.current.scrollHeight,
          behavior: "smooth",
        })
        setShouldScroll(false)
      }, 100)
    }
  }, [visibleCount, shouldScroll])

  const hasMore = visibleCount < filteredAirlines.length
  const showScrollbar = visibleCount > NUMBER_OF_VIEWS
  const visibleAirlines = filteredAirlines.slice(0, visibleCount)

  return (
    <div ref={sectionRef} className="mb-6 relative">
      <p className="flex items-center gap-1">
        <img src={PlaneImg} alt="" className="w-[17px] h-[17px]" />
        <span className="text-gray-700 font-medium">{t("admin.airlines")}</span>
      </p>

      <div className="flex gap-2 mb-3">
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} />

        <label className="flex items-center gap-1.5 px-2">
          {/* <motion.input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleAirlineChange("all")}
            className="cursor-pointer w-4 h-4 border-gray-300 rounded accent-green-600"
            whileTap={{ scale: 0.8 }}
            animate={{ scale: isAllSelected ? 1.2 : 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          /> */}

          <motion.input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleAirlineChange("all")}
            className="w-4 h-4 border-gray-300 rounded accent-second-success focus:ring-second-success cursor-pointer"
            whileTap={{ scale: 0.8 }}
            animate={{ scale: isAllSelected ? 1.2 : 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          />
          <span className="text-xs text-gray-700 whitespace-nowrap">
            {t("admin.log.status.select.all")}
          </span>
        </label>
      </div>

      <div
        ref={listContainerRef}
        className={showScrollbar ? "overflow-y-auto pr-2" : ""}
        style={{
          maxHeight: showScrollbar ? `${MAX_HEIGHT}px` : "none",
        }}
      >
        <ItemsFlexWrapper>
          {visibleAirlines.map((airline, index) => {
            const code = airline.iata
            const logoUrl = `https://images.kiwi.com/airlines/64/${code}.png`
            const fallbackUrl = `https://airlabs.co/img/airline/m/${code}.png`

            return (
              <AnimatedList
                key={code}
                isVisible={true}
                index={index}
                delayFromIndex={!debouncedSearch ? 0.03 : 0}
                disableLayout={!!debouncedSearch}
              >
                <CircularCheckbox checked={selectedCodes.includes(code)}>
                  <div
                    onClick={() => handleAirlineChange(code)}
                    className="ml-2 flex gap-1 items-center cursor-pointer"
                  >
                    <img
                      src={logoUrl}
                      alt={airline.name}
                      onError={(e) => {
                        e.currentTarget.src = fallbackUrl
                      }}
                      className="w-3.5 h-3.5"
                    />
                    <span className="text-dark-gray text-[15px]">{airline.name}</span>
                  </div>
                </CircularCheckbox>
              </AnimatedList>
            )
          })}
        </ItemsFlexWrapper>
      </div>

      {airlines.length > NUMBER_OF_VIEWS && hasMore && (
        <ShowMore showAll={false} onClick={handleShowMore} />
      )}
      <div className="absolute -bottom-3 left-0 right-0 border-b border-light-gray/25"></div>
    </div>
  )
}

export default Airlines
